import { useState, useRef, useCallback } from "react";
import { ImagePlus, X, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImageDropzoneProps {
  onFileSelect: (file: File | null) => void;
  preview?: string | null;
  accept?: string;
  label?: string;
  hint?: string;
  aspectRatio?: string;
  className?: string;
  compact?: boolean;
}

const ImageDropzone = ({
  onFileSelect,
  preview,
  accept = "image/*",
  label = "Arraste uma imagem ou clique para enviar",
  hint = "PNG, JPG ou WEBP",
  aspectRatio = "aspect-[16/6]",
  className,
  compact = false,
}: ImageDropzoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onFileSelect(file);
  };

  const clearFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileSelect(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (preview) {
    return (
      <div className={cn("relative group rounded-xl overflow-hidden border border-border", aspectRatio, className)}>
        <img src={preview} alt="Preview" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <button
            type="button"
            onClick={clearFile}
            className="opacity-0 group-hover:opacity-100 transition-opacity bg-destructive text-destructive-foreground rounded-full p-2 shadow-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-card/90 backdrop-blur-sm text-foreground rounded-lg px-3 py-1.5 text-xs font-medium shadow-lg border border-border hover:bg-card"
        >
          Trocar imagem
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div
      onClick={() => fileInputRef.current?.click()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        "relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200",
        isDragging
          ? "border-primary bg-primary/5 scale-[1.01]"
          : "border-border hover:border-primary/50 hover:bg-muted/30",
        compact ? "p-4" : aspectRatio,
        "flex flex-col items-center justify-center gap-2",
        className,
      )}
    >
      <div className={cn(
        "rounded-full flex items-center justify-center transition-colors",
        isDragging ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
        compact ? "w-10 h-10" : "w-12 h-12",
      )}>
        {isDragging ? (
          <Upload className={compact ? "w-4 h-4" : "w-5 h-5"} />
        ) : (
          <ImagePlus className={compact ? "w-4 h-4" : "w-5 h-5"} />
        )}
      </div>
      <div className="text-center">
        <p className={cn(
          "font-medium",
          isDragging ? "text-primary" : "text-muted-foreground",
          compact ? "text-xs" : "text-sm",
        )}>
          {isDragging ? "Solte a imagem aqui" : label}
        </p>
        {!isDragging && hint && (
          <p className={cn("text-muted-foreground/60", compact ? "text-[10px]" : "text-xs")}>{hint}</p>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export { ImageDropzone };
